// Keyboard, touch and controller input, turned into one input byte per simulation tick.
// Held keys are sampled each tick; dash and attack are "edges" that fire once on the tick after the press.
import { IN } from '../../../shared/sim.js';

const KEYS: Record<string, string> = {
  ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down', ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
  ShiftLeft: 'run', ShiftRight: 'run', Space: 'jump', KeyX: 'attack', KeyJ: 'attack', KeyC: 'dash', KeyK: 'dash', KeyE: 'act', KeyZ: 'act', KeyR: 'mount',
};

export class Input {
  private held = new Set<string>();
  private edges = 0;
  runToggle = false;
  /** Set by the game while you're flying: then holding dash means "descend" rather than one dash. */
  flying = false;
  onAct: () => void = () => {};
  onAttackPress: () => boolean = () => false;   // return true to swallow (e.g. to advance dialogue)
  onChatKey: (slash: boolean) => void = () => {};
  onBagKey: () => void = () => {};
  onPartyKey: () => void = () => {};
  enabled = () => true;
  /** A controller button was pressed (a, b, x, y, lb, rb, start, back, up, down, left, right): return true if
   *  the game used it (a battle, a menu), so the usual meaning is skipped. */
  onPad: (btn: string) => boolean = () => false;
  onMenuKey: () => void = () => {};
  /** In a battle the d-pad is the action bar; the stick still moves you. */
  dpadMoves: () => boolean = () => true;
  onMapKey: () => void = () => {};
  private padWas: boolean[] = [];
  private padDir = 0;
  /** The thumb stick (touch): its direction bits, and whether it's pushed to the edge (run). */
  private stickDir = 0;
  /** Pinch on the game: zoom the camera (`onZoom` gets the new zoom from the zoom at the start of the pinch). */
  onZoom: (z: number) => void = () => {};
  onZoomReset: () => void = () => {};
  zoom = () => 1;

  /** Read the first connected controller (standard mapping, Xbox names): left stick or d-pad to move, A to
   *  talk / pick up / confirm, X to attack, B to dash, Y to jump, RB to ride, LT (or a full push) to run,
   *  Start for your character window, Back / View for the map. */
  private pollPad(): number {
    const pads = navigator.getGamepads?.() ?? [], p = [...pads].find(g => g && g.connected);
    if (!p) { this.padDir = 0; return 0; }
    const pressed = (i: number) => !!p.buttons[i]?.pressed, was = this.padWas, NAMES: [number, string][] = [[0, 'a'], [1, 'b'], [2, 'x'], [3, 'y'], [4, 'lb'], [5, 'rb'], [9, 'start'], [8, 'back'], [12, 'up'], [13, 'down'], [14, 'left'], [15, 'right']];
    let bits = 0;
    for (const [i, name] of NAMES) {
      const now = pressed(i), edge = now && !was[i]; was[i] = now;
      if (!edge) continue;
      if (this.onPad(name)) continue;
      if (name === 'a') this.onAct();
      else if (name === 'x') { if (!this.onAttackPress()) bits |= IN.attack; }
      else if (name === 'b') bits |= IN.dash;
      else if (name === 'y') { if (!this.onAttackPress()) bits |= IN.jump; }
      else if (name === 'rb') bits |= IN.mount;
      else if (name === 'start') this.onMenuKey();
      else if (name === 'back') this.onMapKey();
    }
    if (pressed(3)) bits |= IN.jump;
    if (this.flying && pressed(1)) bits |= IN.dash;
    const ax = p.axes[0] ?? 0, ay = p.axes[1] ?? 0, mag = Math.hypot(ax, ay), dead = 0.35;
    let d = 0;
    if (mag > dead) { if (ax > dead) d |= IN.right; if (ax < -dead) d |= IN.left; if (ay > dead) d |= IN.down; if (ay < -dead) d |= IN.up; }
    if (this.dpadMoves()) { if (pressed(12)) d |= IN.up; if (pressed(13)) d |= IN.down; if (pressed(14)) d |= IN.left; if (pressed(15)) d |= IN.right; }
    if (d && (mag > 0.92 || (p.buttons[6]?.value ?? 0) > 0.4)) d |= IN.run;
    this.padDir = d;
    return bits;
  }

  constructor() {
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      if (e.code === 'Enter' || e.code === 'Slash') { e.preventDefault(); this.onChatKey(e.code === 'Slash'); return; }
      if ((e.code === 'KeyB' || e.code === 'KeyI') && !e.repeat) { e.preventDefault(); this.onBagKey(); return; }
      if (e.code === 'KeyP' && !e.repeat) { e.preventDefault(); this.onPartyKey(); return; }
      const k = KEYS[e.code]; if (!k) return;
      e.preventDefault();
      if (e.repeat && (k === 'attack' || k === 'dash' || k === 'act' || k === 'mount' || k === 'jump')) { if (k === 'jump') this.held.add(k); return; }
      if (k === 'act') return this.onAct();
      if (k === 'attack') { if (!this.onAttackPress()) this.edges |= IN.attack; return; }
      if (k === 'jump') { if (!this.onAttackPress()) { this.held.add('jump'); this.edges |= IN.jump; } return; }   // even a very quick tap jumps   // Space also turns the page in a conversation
      if (k === 'mount') { this.edges |= IN.mount; return; }
      if (k === 'dash') { this.edges |= IN.dash; this.held.add('dashHold'); return; }
      this.held.add(k);
    });
    addEventListener('keyup', e => { const k = KEYS[e.code]; if (k) { this.held.delete(k); if (k === 'dash') this.held.delete('dashHold'); } });
    addEventListener('blur', () => this.held.clear());
    document.querySelectorAll<HTMLElement>('.pad [data-k]').forEach(b => {
      const k = b.dataset.k!;
      b.addEventListener('pointerdown', e => {
        e.preventDefault(); try { b.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
        if (k === 'act') this.onAct();
        else if (k === 'chat') this.onChatKey(false);
        else if (k === 'bag') this.onBagKey();
        else if (k === 'attack') { if (!this.onAttackPress()) this.edges |= IN.attack; }
        else if (k === 'dash') { this.edges |= IN.dash; this.held.add('dashHold'); }
        else if (k === 'mount') this.edges |= IN.mount;
        else if (k === 'jump') { if (!this.onAttackPress()) { this.held.add('jump'); this.edges |= IN.jump; } }
        else if (k === 'run') { this.runToggle = !this.runToggle; b.setAttribute('aria-pressed', String(this.runToggle)); }
        else this.held.add(k);
      });
      const up = () => { this.held.delete(k); if (k === 'dash') this.held.delete('dashHold'); };
      b.addEventListener('pointerup', up); b.addEventListener('pointercancel', up); b.addEventListener('lostpointercapture', up);
    });
    this.touchStick();
    this.noBrowserZoom();
  }

  /** A floating thumb stick: put your thumb down anywhere on the movement half of the screen (the left, or the
   *  right when left-handed) and the stick appears under it; slide to walk any of eight ways, push to the rim to
   *  run. A second finger on the game turns it into a pinch (camera zoom); two fingers tapped twice resets it. */
  private touchStick(): void {
    const pad = document.querySelector<HTMLElement>('.pad'), home = document.querySelector<HTMLElement>('.stick-home'), cv = document.getElementById('world');
    if (!pad || !home || !cv) return;
    const float = document.createElement('div'); float.className = 'stick-float'; float.hidden = true;
    const knob = document.createElement('div'); knob.className = 'stick-knob'; float.append(knob); document.body.append(float);
    const shown = () => getComputedStyle(pad).display !== 'none';
    const lefty = () => pad.classList.contains('lefty');
    let id: number | null = null, ox = 0, oy = 0;
    const R = () => Math.max(34, home.getBoundingClientRect().width * 0.42);
    const touches = new Map<number, { x: number; y: number; t: number; x0: number; y0: number }>();
    let pinch: { d0: number; z0: number } | null = null, twoAt = 0, twoMoved = false, lastTwoTap = 0;
    const stop = () => { id = null; this.stickDir = 0; float.hidden = true; home.classList.remove('active'); };
    const set = (x: number, y: number) => {
      const r = R(); let dx = x - ox, dy = y - oy; const d = Math.hypot(dx, dy);
      if (d > r) { dx *= r / d; dy *= r / d; }
      knob.style.transform = `translate(${dx}px, ${dy}px)`;
      const m = Math.min(1, d / r); let bits = 0;
      if (m > 0.22) {
        const nx = dx / (Math.hypot(dx, dy) || 1), ny = dy / (Math.hypot(dx, dy) || 1);
        if (nx > 0.38) bits |= IN.right; if (nx < -0.38) bits |= IN.left; if (ny > 0.38) bits |= IN.down; if (ny < -0.38) bits |= IN.up;
        if (m > 0.92) bits |= IN.run;
      }
      this.stickDir = bits;
    };
    const start = (e: PointerEvent) => {
      ox = e.clientX; oy = e.clientY; id = e.pointerId;
      const r = R(); float.style.width = float.style.height = `${r * 2}px`;
      float.style.left = `${ox - r}px`; float.style.top = `${oy - r}px`; float.hidden = false; home.classList.add('active');
      knob.style.transform = 'translate(0px, 0px)'; this.stickDir = 0;
    };
    const onMoveHalf = (x: number) => lefty() ? x > innerWidth / 2 : x < innerWidth / 2;
    // the stick's own spot (always), and the game itself on the movement half
    home.addEventListener('pointerdown', e => { e.preventDefault(); if (id == null) { try { home.setPointerCapture(e.pointerId); } catch { /* */ } start(e); } });
    cv.addEventListener('pointerdown', e => {
      if (e.pointerType === 'mouse' || !shown()) return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now(), x0: e.clientX, y0: e.clientY });
      if (touches.size === 2) {   // a second finger: a pinch, not a walk
        stop();
        const [a, b] = [...touches.values()]; pinch = { d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, z0: this.zoom() };
        twoAt = performance.now(); twoMoved = false; return;
      }
      if (touches.size === 1 && id == null && onMoveHalf(e.clientX)) { try { cv.setPointerCapture(e.pointerId); } catch { /* */ } start(e); }
    });
    addEventListener('pointermove', e => {
      const t = touches.get(e.pointerId); if (t) { t.x = e.clientX; t.y = e.clientY; if (Math.hypot(t.x - t.x0, t.y - t.y0) > 12) twoMoved = true; }
      if (pinch && touches.size >= 2) { const [a, b] = [...touches.values()]; this.onZoom(pinch.z0 * Math.hypot(a.x - b.x, a.y - b.y) / pinch.d0); return; }
      if (e.pointerId === id) set(e.clientX, e.clientY);
    });
    const end = (e: PointerEvent) => {
      if (touches.has(e.pointerId)) {
        if (touches.size === 2 && pinch) {   // two fingers lifted after a quick still tap: twice in a row resets the zoom
          const now = performance.now();
          if (!twoMoved && now - twoAt < 300) { if (now - lastTwoTap < 450) { this.onZoomReset(); lastTwoTap = 0; } else lastTwoTap = now; }
        }
        touches.delete(e.pointerId); if (touches.size < 2) pinch = null;
      }
      if (e.pointerId === id) stop();
    };
    addEventListener('pointerup', end); addEventListener('pointercancel', end);
    addEventListener('blur', () => { stop(); touches.clear(); pinch = null; });
  }

  /** The page itself never zooms (a pinch is the game's camera); ctrl + scroll (a trackpad pinch) zooms the camera too. */
  private noBrowserZoom(): void {
    for (const ev of ['gesturestart', 'gesturechange', 'gestureend']) document.addEventListener(ev, e => e.preventDefault(), { passive: false } as AddEventListenerOptions);
    document.addEventListener('touchmove', e => { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });
    let lastEnd = 0;
    document.addEventListener('touchend', e => { const now = Date.now(); if (now - lastEnd < 320 && (e.target as HTMLElement)?.closest?.('.pad, #world')) e.preventDefault(); lastEnd = now; }, { passive: false });   // no double-tap zoom
    addEventListener('wheel', e => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if ((e.target as HTMLElement)?.id === 'world') this.onZoom(this.zoom() * Math.exp(-e.deltaY * 0.01));
    }, { passive: false });
  }

  /** The input byte for this tick. */
  sample(): number {
    const pad = this.pollPad();
    if (!this.enabled()) { this.edges = 0; return 0; }
    let b = this.edges | pad | this.padDir | this.stickDir; this.edges = 0;
    const h = this.held;
    if (h.has('up')) b |= IN.up; if (h.has('down')) b |= IN.down; if (h.has('left')) b |= IN.left; if (h.has('right')) b |= IN.right;
    if (h.has('run') || this.runToggle) b |= IN.run;
    if (h.has('jump')) b |= IN.jump;
    if (this.flying && h.has('dashHold')) b |= IN.dash;   // flying: hold dash to come down
    return b;
  }
  clear(): void { this.held.clear(); this.edges = 0; this.stickDir = 0; }
}
